import type {
  AddListItemRequest,
  ListSummary,
  MealDetail,
  MealIngredient,
  SponsoredItem,
} from '@lista/shared-validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCore } from '../context';
import { queryKeys } from '../query-keys';

/** Meals are reference data: cached long, localized by the current language. */
const STALE = 10 * 60_000;

export function useCuisines() {
  const { services, locale } = useCore();
  return useQuery({
    queryKey: [...queryKeys.cuisines, locale],
    queryFn: services.meals.cuisines,
    staleTime: STALE,
  });
}

export function useCuisine(id: string) {
  const { services, locale } = useCore();
  return useQuery({
    queryKey: [...queryKeys.cuisine(id), locale],
    queryFn: () => services.meals.cuisine(id),
    staleTime: STALE,
    enabled: !!id,
  });
}

export function useCuisineMeals(cuisineId: string) {
  const { services, locale } = useCore();
  return useQuery({
    queryKey: [...queryKeys.cuisineMeals(cuisineId), locale],
    queryFn: () => services.meals.mealsOf(cuisineId),
    staleTime: STALE,
    enabled: !!cuisineId,
  });
}

export function useMeal(id: string) {
  const { services, locale } = useCore();
  return useQuery({
    queryKey: [...queryKeys.meal(id), locale],
    queryFn: () => services.meals.meal(id),
    staleTime: STALE,
    enabled: !!id,
  });
}

/** The lines to send for the chosen ingredients: catalog item, recipe amount and unit. */
export function ingredientLines(
  ingredients: readonly Pick<MealIngredient, 'catalogItemId' | 'quantity' | 'unit'>[],
) {
  return ingredients.map((i) => ({
    catalogItemId: i.catalogItemId,
    quantity: i.quantity,
    unit: i.unit,
    mode: 'merge' as const,
  }));
}

/**
 * Adds the chosen ingredients of a meal to a list in one request (all or nothing). Amounts
 * merge with lines already on the list.
 */
export function useAddIngredients() {
  const { services } = useCore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      listId,
      ingredients,
    }: {
      listId: string;
      ingredients: readonly MealIngredient[];
      meal?: MealDetail;
    }) => services.listItems.addMany(listId, ingredientLines(ingredients)),
    onSuccess: (_items, { listId }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.lists.items(listId) });
      void qc.invalidateQueries({ queryKey: queryKeys.lists.detail(listId) });
      void qc.invalidateQueries({ queryKey: queryKeys.lists.dashboards });
      void qc.invalidateQueries({ queryKey: queryKeys.recent });
    },
  });
}

export interface AddIngredientsToListsVars {
  /** Existing lists to add to. */
  lists: readonly Pick<ListSummary, 'id' | 'name'>[];
  /** Also create a list with this name (default colour) and add to it. */
  newListName?: string;
  ingredients: readonly Pick<MealIngredient, 'catalogItemId' | 'quantity' | 'unit'>[];
}

export interface AddLinesToListsVars {
  lists: readonly Pick<ListSummary, 'id' | 'name'>[];
  newListName?: string;
  lines: Array<Partial<AddListItemRequest>>;
}

export interface AddIngredientsToListsResult {
  added: Pick<ListSummary, 'id' | 'name'>[];
  failed: Array<{ list: Pick<ListSummary, 'id' | 'name'>; error: unknown }>;
  /** The list created from `newListName`, if any. */
  created?: ListSummary;
}

/** The lines to send for the chosen items of a sponsored list (catalog or brand products). */
export function sponsoredLines(
  items: readonly SponsoredItem[],
): Array<Partial<AddListItemRequest>> {
  return items.map((i) =>
    i.catalogItemId
      ? {
          catalogItemId: i.catalogItemId,
          quantity: i.quantity,
          unit: i.unit,
          note: i.note ?? undefined,
          mode: 'merge' as const,
        }
      : { customName: i.name, quantity: i.quantity, unit: i.unit, note: i.note ?? undefined },
  );
}

/**
 * Adds lines to several lists at once, optionally creating a new list first (that failing
 * rejects the whole call). One all-or-nothing request per list; a list that fails is reported
 * in `failed` and does not stop the others. Amounts merge with existing lines. Recipes and
 * sponsored lists both add this way.
 */
export function useAddLinesToLists() {
  const { services, newId } = useCore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      lists,
      newListName,
      lines,
    }: AddLinesToListsVars): Promise<AddIngredientsToListsResult> => {
      const name = newListName?.trim();
      const created = name
        ? await services.lists.create({ name, color: 'DEFAULT', id: newId() })
        : undefined;
      if (created) qc.setQueryData(queryKeys.lists.detail(created.id), created);
      const targets = created ? [...lists, created] : [...lists];
      const settled = await Promise.allSettled(
        targets.map((l) => services.listItems.addMany(l.id, lines)),
      );
      const result: AddIngredientsToListsResult = { added: [], failed: [], created };
      settled.forEach((s, i) => {
        const list = targets[i]!;
        if (s.status === 'fulfilled') result.added.push({ id: list.id, name: list.name });
        else result.failed.push({ list: { id: list.id, name: list.name }, error: s.reason });
        void qc.invalidateQueries({ queryKey: queryKeys.lists.items(list.id) });
        void qc.invalidateQueries({ queryKey: queryKeys.lists.detail(list.id) });
      });
      return result;
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.lists.dashboards });
      void qc.invalidateQueries({ queryKey: queryKeys.recent });
    },
  });
}

/** Adds the chosen ingredients of a meal to several lists: {@link useAddLinesToLists}. */
export function useAddIngredientsToLists() {
  const add = useAddLinesToLists();
  const toLines = ({ ingredients, ...rest }: AddIngredientsToListsVars): AddLinesToListsVars => ({
    ...rest,
    lines: ingredientLines(ingredients),
  });
  return {
    ...add,
    mutate: (vars: AddIngredientsToListsVars, options?: Parameters<typeof add.mutate>[1]) =>
      add.mutate(toLines(vars), options),
    mutateAsync: (vars: AddIngredientsToListsVars) => add.mutateAsync(toLines(vars)),
  };
}
